import apiClient from '../client';

// ── Types ──────────────────────────────────────────────────────────

export type AccessLevel = 'none' | 'read' | 'write';

export interface RoleSummary {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  is_template: boolean;
  cloned_from_role_id: string | null;
  requires_2fa: boolean;
  is_external: boolean;
  expires_at: string | null;
  purpose_of_access: string | null;
  member_count: number;
  permission_count: number;
  tier_locked: boolean;
}

export interface FieldPermissionRow {
  field_category: string;
  access_level: AccessLevel;
  company_id: string | null;
}

export interface ScopeRow {
  scope_type: string;
  scope_value: Record<string, unknown>;
  modifier: 'include' | 'exclude';
  company_id: string | null;
}

export interface DelegationRow {
  action_type: string;
  currency: string;
  amount_ceiling: number | null;
  company_id: string | null;
}

export interface RoleDetail extends RoleSummary {
  permission_codes: string[];
  field_permissions: FieldPermissionRow[];
  scopes: ScopeRow[];
  delegations: DelegationRow[];
  /** Optimistic-concurrency token — echo back as expected_version on save. */
  version_id: number;
}

export interface RoleMember {
  user_id: string;
  email: string;
  full_name: string;
  company_id: string;
  is_group_level: boolean;
  last_login_at: string | null;
}

export interface SodViolation {
  rule_name: string;
  severity: 'warn' | 'block';
  description: string;
  rationale: string | null;
  triggering_codes: string[];
}

export interface SodRule {
  id: string;
  rule_name: string;
  description: string;
  incompatible_codes: string[];
  severity: 'warn' | 'block';
  rationale: string | null;
}

export interface UcrPermissions {
  ucr_id: string;
  user_id: string;
  user_email: string | null;
  user_full_name: string | null;
  company_id: string;
  role_id: string;
  role_name: string | null;
  is_group_level: boolean;
  is_primary: boolean;
  can_manage_billing: boolean;
  permission_count: number;
  permission_codes: string[];
}

export interface DenialLogEntry {
  id: string;
  user_id: string;
  role_id: string | null;
  attempted_path: string;
  attempted_action: string;
  permission_code: string | null;
  field_category: string | null;
  scope_violated: string | null;
  ip_address: string | null;
  created_at: string;
}

// ── API ────────────────────────────────────────────────────────────

export const adminRolesApi = {
  list: () => apiClient.get<RoleSummary[]>('/admin/roles'),

  detail: (roleId: string) => apiClient.get<RoleDetail>(`/admin/roles/${roleId}`),

  members: (roleId: string) => apiClient.get<RoleMember[]>(`/admin/roles/${roleId}/members`),

  clone: (data: { source_role_id: string; name: string; description?: string }) =>
    apiClient.post<RoleDetail>('/admin/roles/clone', data),

  update: (
    roleId: string,
    data: {
      name?: string;
      description?: string;
      requires_2fa?: boolean;
      is_external?: boolean;
      expires_at?: string | null;
      purpose_of_access?: string;
    },
  ) => apiClient.patch<RoleDetail>(`/admin/roles/${roleId}`, data),

  replacePermissions: (roleId: string, permissionCodes: string[], expectedVersion?: number) =>
    apiClient.put<{ ok: boolean; sod_violations: SodViolation[] }>(
      `/admin/roles/${roleId}/permissions`,
      expectedVersion === undefined
        ? { permission_codes: permissionCodes }
        : { permission_codes: permissionCodes, expected_version: expectedVersion },
    ),

  bulkAssignPermissions: (data: {
    role_ids: string[];
    permission_codes: string[];
    action: 'add' | 'remove';
  }) =>
    apiClient.post<{
      action: string;
      roles_affected: number;
      permissions_affected: number;
      rows_changed: number;
      sod_violations: SodViolation[];
    }>('/admin/roles/bulk-permissions', data),

  replaceFieldPermissions: (
    roleId: string,
    fieldPermissions: Record<string, AccessLevel>,
    expectedVersion?: number,
  ) =>
    apiClient.put<{ ok: boolean }>(`/admin/roles/${roleId}/field-permissions`, {
      field_permissions: fieldPermissions,
      ...(expectedVersion === undefined ? {} : { expected_version: expectedVersion }),
    }),

  replaceScopes: (roleId: string, scopes: ScopeRow[], expectedVersion?: number) =>
    apiClient.put<{ ok: boolean }>(`/admin/roles/${roleId}/scopes`, {
      scopes,
      ...(expectedVersion === undefined ? {} : { expected_version: expectedVersion }),
    }),

  delete: (roleId: string) => apiClient.delete<{ ok: boolean }>(`/admin/roles/${roleId}`),

  sodRules: () => apiClient.get<SodRule[]>('/admin/sod-rules'),

  denialLog: (limit = 50, offset = 0) =>
    apiClient.get<{ total_returned: number; items: DenialLogEntry[] }>(
      `/admin/denial-log?limit=${limit}&offset=${offset}`,
    ),

  ucrPermissions: (ucrId: string) =>
    apiClient.get<UcrPermissions>(`/admin/ucr/${ucrId}/permissions`),
};
